/**
 * Opportunities (shortlists list) — port pixel-perfect del handoff
 * `scouting-wizard-client-portal/project/Opportunities.html` (Diego UX / Claude Design).
 * SW-147.
 *
 * Estructura del handoff:
 *  - Crumb bar (sólo "Opportunities" en client portal — el botón "+ New" del
 *    handoff es flujo recruiter, NO se porta).
 *  - H1 "Opportunities" + lede.
 *  - Toolbar con filter tabs: All / Active / Closed (omitido "In review"
 *    porque el BE solo expone status Shared=Active y Closed; cuando agregue
 *    in-review se suma acá).
 *  - Grid auto-fill 360px de cards.
 *  - Card con:
 *      · Follow button (top-right, local state — no BE follow toggle aún)
 *      · "Following" tag (visible cuando followed)
 *      · Head: client-mark gradient (4 variantes por hash del slug) + client
 *        line "Client · Industry" + title del shortlist
 *      · Meta chips (placeholders: hires/location/rate/quarter — sin BE)
 *      · Status pill (active/closed; review reservado para futuro)
 *      · Stats footer: Presented (candidateCount real) + New/Seen ("—" hasta
 *        SW-145 bulk endpoint) + "Open →" link
 *
 * Datos reales: id, name, sharedAt, status, candidateCount.
 * Placeholders honestos (no fake): client name + industry, meta chips,
 * per-card New/Seen stats. Documentados como follow-ups SW-148.
 */
import { useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Alert, Box, Button, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { tokens } from '../../theme/tokens';
import { useNotify } from '../../lib/notify';
import { useShortlists } from './useShortlists';
import type { ShortlistListItem, ShortlistStatus } from './shortlistsApi';
import {
  parseShortlistName,
  hashIndex,
  formatRelativeDate,
} from './_helpers';

const PAGE_SIZE = 20;

// SW-147: client-mark gradients portados del handoff (cm-1..cm-4). Inline
// — los gradients no son tokens semánticos (no son colores reutilizables),
// son variantes determinísticas para avatares.
const CLIENT_MARK_GRADIENTS = [
  'linear-gradient(140deg, #1f6feb, #0a4dbf)',
  'linear-gradient(140deg, #c2410c, #7c2d12)',
  'linear-gradient(140deg, #15803d, #0f5d2e)',
  'linear-gradient(140deg, #7c3aed, #4c1d95)',
] as const;

type FilterKey = 'all' | 'active' | 'closed';

const FILTER_KEYS: ReadonlyArray<FilterKey> = ['all', 'active', 'closed'];

// SW-147 code-review M-2: alias nombrados para los magic numbers del enum
// `ShortlistStatus` del BE (1=Shared/Active, 2=Closed). Evita comparaciones
// `=== 1`/`=== 2` dispersas; si el BE introduce un tercer estado, el refactor
// queda localizado a estas constantes.
const STATUS_ACTIVE: ShortlistStatus = 1;
const STATUS_CLOSED: ShortlistStatus = 2;

export default function OpportunitiesPage() {
  const { t, i18n } = useTranslation();
  const [pageNumber, setPageNumber] = useState(1);
  const [filter, setFilter] = useState<FilterKey>('all');
  // SW-147: Follow persistence es local-only (no BE). Survive la sesión via
  // localStorage para que QA pueda probar el toggle entre navegaciones.
  const [followed, setFollowed] = useFollowedShortlists();
  const query = useShortlists({ pageNumber, pageSize: PAGE_SIZE });

  const items = query.data?.items ?? [];

  const counts = useMemo(
    () => ({
      all: items.length,
      active: items.filter((i) => i.status === STATUS_ACTIVE).length,
      closed: items.filter((i) => i.status === STATUS_CLOSED).length,
    }),
    [items],
  );

  const filteredItems = useMemo(() => {
    if (filter === 'all') return items;
    if (filter === 'active') return items.filter((i) => i.status === STATUS_ACTIVE);
    if (filter === 'closed') return items.filter((i) => i.status === STATUS_CLOSED);
    return items;
  }, [items, filter]);

  return (
    <Box
      data-testid="opportunities-page"
      sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
    >
      <Breadcrumb />

      <Box>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: 28, md: 36 },
            fontWeight: 700,
            letterSpacing: '-0.025em',
            m: '8px 0 6px',
          }}
        >
          {t('opportunities.title')}
        </Typography>
        <Typography
          sx={{
            color: tokens.color.muted,
            fontSize: 14.5,
            mb: '8px',
          }}
        >
          {t('opportunities.subtitle')}
        </Typography>
      </Box>

      {query.isSuccess && items.length > 0 && (
        <Toolbar active={filter} onChange={setFilter} counts={counts} />
      )}

      {query.isPending && <LoadingSkeleton />}

      {query.isError && <ErrorState onRetry={() => void query.refetch()} />}

      {query.isSuccess && items.length === 0 && <EmptyState />}

      {query.isSuccess && items.length > 0 && filteredItems.length === 0 && (
        <FilteredEmptyState onClear={() => setFilter('all')} />
      )}

      {query.isSuccess && filteredItems.length > 0 && (
        <>
          <Box
            role="tabpanel"
            id="tabpanel-opportunities"
            aria-labelledby={`tab-opportunities-${filter}`}
            data-testid="opportunities-list"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
              gap: '14px',
            }}
          >
            {filteredItems.map((item) => (
              <ShortlistCard
                key={item.id}
                item={item}
                locale={i18n.language}
                isFollowed={followed.has(item.id)}
                onToggleFollow={() => {
                  setFollowed((prev) => {
                    const next = new Set(prev);
                    if (next.has(item.id)) {
                      next.delete(item.id);
                    } else {
                      next.add(item.id);
                    }
                    return next;
                  });
                }}
              />
            ))}
          </Box>
          <Pagination
            page={pageNumber}
            pageSize={PAGE_SIZE}
            total={query.data.totalCount}
            isFetching={query.isFetching}
            onChange={setPageNumber}
          />
        </>
      )}
    </Box>
  );
}

// ============================================================================
// BREADCRUMB
// ============================================================================

function Breadcrumb() {
  const { t } = useTranslation();
  return (
    <Box
      component="nav"
      aria-label={t('opportunities.list.breadcrumbAria')}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        pt: '10px',
        pb: '18px',
        color: tokens.color.muted,
        fontSize: 13,
        borderBottom: `1px solid ${tokens.color.line}`,
      }}
    >
      <Box
        component="span"
        sx={{
          color: tokens.color.ink,
          fontWeight: 600,
          padding: '4px 8px',
        }}
      >
        {t('opportunities.title')}
      </Box>
    </Box>
  );
}

// ============================================================================
// TOOLBAR (FILTER TABS)
// ============================================================================

function Toolbar({
  active,
  onChange,
  counts,
}: {
  active: FilterKey;
  onChange: (k: FilterKey) => void;
  counts: { all: number; active: number; closed: number };
}) {
  const { t } = useTranslation();

  // SW-147: WAI-ARIA tablist pattern (Arrow keys + Home/End + tabIndex dinámico
  // + aria-controls). Mismo patrón que TalentDetailPage/OpportunityDetailPage.
  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>, idx: number) {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      onChange(FILTER_KEYS[(idx + 1) % FILTER_KEYS.length]!);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      onChange(FILTER_KEYS[(idx - 1 + FILTER_KEYS.length) % FILTER_KEYS.length]!);
    } else if (e.key === 'Home') {
      e.preventDefault();
      onChange(FILTER_KEYS[0]!);
    } else if (e.key === 'End') {
      e.preventDefault();
      onChange(FILTER_KEYS[FILTER_KEYS.length - 1]!);
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap',
      }}
    >
      <Box
        role="tablist"
        aria-label={t('opportunities.list.filterAria')}
        data-testid="opportunities-toolbar"
        sx={{
          display: 'inline-flex',
          gap: '4px',
          padding: '4px',
          background: tokens.color.surface,
          borderRadius: '10px',
          border: `1px solid ${tokens.color.line}`,
        }}
      >
        {FILTER_KEYS.map((key, idx) => {
          const isActive = key === active;
          const count = counts[key];
          return (
            <Box
              key={key}
              component="button"
              role="tab"
              id={`tab-opportunities-${key}`}
              aria-selected={isActive}
              aria-controls="tabpanel-opportunities"
              tabIndex={isActive ? 0 : -1}
              data-testid={`opportunities-filter-${key}`}
              onClick={() => onChange(key)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              sx={{
                appearance: 'none',
                border: 0,
                background: isActive ? tokens.color.surface2 : 'transparent',
                color: isActive ? tokens.color.ink : tokens.color.muted,
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: isActive ? 600 : 500,
                padding: '6px 12px',
                borderRadius: '7px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
                '&:hover': {
                  color: tokens.color.ink,
                },
              }}
            >
              {t(`opportunities.list.filter.${key}`)}
              <Box
                component="span"
                sx={{
                  fontFamily: tokens.font.mono,
                  fontSize: 10.5,
                  padding: '1px 6px',
                  background: isActive ? tokens.color.ink : tokens.color.surface,
                  color: isActive ? tokens.color.inkInverse : tokens.color.muted,
                  border: `1px solid ${isActive ? tokens.color.ink : tokens.color.line}`,
                  borderRadius: tokens.size.radiusPill,
                }}
              >
                {count}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ============================================================================
// SHORTLIST CARD
// ============================================================================

function ShortlistCard({
  item,
  locale,
  isFollowed,
  onToggleFollow,
}: {
  item: ShortlistListItem;
  locale: string;
  isFollowed: boolean;
  onToggleFollow: () => void;
}) {
  const { t } = useTranslation();
  const notify = useNotify();
  const gradient = CLIENT_MARK_GRADIENTS[hashIndex(item.id, CLIENT_MARK_GRADIENTS.length)]!;
  // SW-147: extraemos el "client name" del shortlist name. Convención del seed:
  // "{ClientName} — {ProjectTitle}". Si no hay separador, todo va como title.
  const { clientLabel, titleLabel } = parseShortlistName(item.name);
  const clientInitial = clientLabel
    ? clientLabel.trim().charAt(0).toUpperCase()
    : titleLabel.trim().charAt(0).toUpperCase();

  function handleFollow(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    onToggleFollow();
    notify.info(
      isFollowed
        ? t('opportunities.list.followToast.unfollowed', { name: item.name })
        : t('opportunities.list.followToast.followed', { name: item.name }),
    );
  }

  return (
    <Box
      component={Link}
      to={`/opportunities/${item.id}`}
      data-testid="opportunity-card"
      data-shortlist-id={item.id}
      data-status={item.status === STATUS_ACTIVE ? 'active' : 'closed'}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        background: tokens.color.surface,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: '14px',
        padding: '20px',
        textDecoration: 'none',
        color: 'inherit',
        boxShadow: '0 1px 0 rgba(15,15,15,.04), 0 8px 24px -12px rgba(15,15,15,.10)',
        transition: 'border-color .15s, transform .1s',
        opacity: item.status === STATUS_CLOSED ? 0.78 : 1,
        '&:hover': {
          borderColor: tokens.color.ink,
        },
      }}
    >
      <FollowButton followed={isFollowed} onClick={handleFollow} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Box
          data-testid="opportunity-client-mark"
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            display: 'grid',
            placeItems: 'center',
            background: gradient,
            color: tokens.color.inkInverse,
            fontWeight: 700,
            fontSize: 15,
            flexShrink: 0,
          }}
        >
          {clientInitial}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              fontFamily: tokens.font.mono,
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: tokens.color.muted,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            data-testid="opportunity-client-name"
          >
            {clientLabel || t('opportunities.list.clientFallback')}
          </Box>
          <Typography
            data-testid="opportunity-name"
            sx={{
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: '-0.015em',
              m: '2px 0 0',
              color: tokens.color.ink,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {titleLabel}
          </Typography>
        </Box>
      </Box>

      <StatusPill status={item.status} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '14px',
          borderTop: `1px dashed ${tokens.color.line}`,
          fontFamily: tokens.font.mono,
          fontSize: 12,
        }}
      >
        <StatCell
          label={t('opportunities.list.stats.presented')}
          value={String(item.candidateCount)}
          testId="opportunity-stat-presented"
        />
        <StatCell
          label={t('opportunities.list.stats.new')}
          value="—"
          testId="opportunity-stat-new"
        />
        <StatCell
          label={t('opportunities.list.stats.seen')}
          value="—"
          testId="opportunity-stat-seen"
        />
        <Box
          component="span"
          data-testid="opportunity-shared-at"
          sx={{
            color: tokens.color.muted,
            fontSize: 10.5,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            alignSelf: 'flex-end',
          }}
        >
          {t('opportunities.sharedAt', { when: formatRelativeDate(item.sharedAt, locale) })}
        </Box>
      </Box>
    </Box>
  );
}

function FollowButton({
  followed,
  onClick,
}: {
  followed: boolean;
  onClick: (e: React.MouseEvent) => void;
}) {
  const { t } = useTranslation();
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      data-testid="opportunity-follow"
      data-followed={followed ? 'true' : 'false'}
      aria-label={
        followed
          ? t('opportunities.list.follow.unfollowAria')
          : t('opportunities.list.follow.followAria')
      }
      title={
        followed
          ? t('opportunities.list.follow.unfollowAria')
          : t('opportunities.list.follow.followAria')
      }
      sx={{
        position: 'absolute',
        top: '12px',
        right: '12px',
        width: 30,
        height: 30,
        borderRadius: '8px',
        background: followed ? tokens.color.accentSoft : 'transparent',
        border: `1px solid ${followed ? tokens.color.accent : tokens.color.line}`,
        color: followed ? tokens.color.accent : tokens.color.muted,
        cursor: 'pointer',
        display: 'grid',
        placeItems: 'center',
        transition: 'background .15s, border-color .15s, color .15s',
        '&:hover': {
          borderColor: tokens.color.accent,
          color: tokens.color.accent,
        },
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 16 16"
        fill={followed ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth={1.6}
        sx={{ width: 14, height: 14 }}
        aria-hidden
      >
        <path d="M4 2.5h8v11l-4-2.5-4 2.5v-11z" />
      </Box>
    </Box>
  );
}

function StatusPill({ status }: { status: ShortlistStatus }) {
  const { t } = useTranslation();
  const isActive = status === STATUS_ACTIVE;
  return (
    <Box
      component="span"
      data-testid="opportunity-status"
      data-status={isActive ? 'active' : 'closed'}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '3px 9px',
        borderRadius: tokens.size.radiusPill,
        background: isActive ? tokens.color.posSoft : tokens.color.surface2,
        color: isActive ? tokens.color.pos : tokens.color.muted,
        border: isActive ? 'none' : `1px solid ${tokens.color.line}`,
        fontFamily: tokens.font.mono,
        fontSize: 11,
        fontWeight: 600,
        width: 'max-content',
      }}
    >
      <Box
        component="span"
        sx={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: isActive ? tokens.color.pos : tokens.color.muted2,
        }}
      />
      {isActive ? t('opportunities.status.shared') : t('opportunities.status.closed')}
    </Box>
  );
}

function StatCell({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId?: string;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        component="span"
        sx={{
          color: tokens.color.muted,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontSize: 10.5,
        }}
      >
        {label}
      </Box>
      <Box
        component="span"
        data-testid={testId}
        sx={{
          fontSize: 18,
          fontWeight: 700,
          color: tokens.color.ink,
          fontFamily: tokens.font.display,
          letterSpacing: '-0.02em',
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

// ============================================================================
// EMPTY / ERROR / LOADING STATES
// ============================================================================

function LoadingSkeleton() {
  return (
    <Box
      data-testid="opportunities-loading"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
        gap: '14px',
      }}
    >
      {Array.from({ length: 4 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            background: tokens.color.surface,
            border: `1px solid ${tokens.color.line}`,
            borderRadius: '14px',
            padding: '20px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Skeleton variant="rounded" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="35%" sx={{ fontSize: 12 }} />
              <Skeleton variant="text" width="65%" sx={{ fontSize: 18 }} />
            </Box>
          </Box>
          <Skeleton variant="text" width="20%" sx={{ fontSize: 11 }} />
          <Box sx={{ display: 'flex', gap: '24px', pt: '14px' }}>
            <Skeleton variant="text" width={40} sx={{ fontSize: 18 }} />
            <Skeleton variant="text" width={40} sx={{ fontSize: 18 }} />
            <Skeleton variant="text" width={40} sx={{ fontSize: 18 }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="opportunities-empty"
      sx={{
        background: tokens.color.surface,
        border: `1px dashed ${tokens.color.line}`,
        borderRadius: '14px',
        padding: '48px 28px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
        {t('opportunities.empty.title')}
      </Typography>
      <Typography
        sx={{
          color: tokens.color.muted,
          fontSize: 14,
          maxWidth: 480,
          mx: 'auto',
        }}
      >
        {t('opportunities.empty.message')}
      </Typography>
    </Box>
  );
}

function FilteredEmptyState({ onClear }: { onClear: () => void }) {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="opportunities-filtered-empty"
      sx={{
        background: tokens.color.surface,
        border: `1px dashed ${tokens.color.line}`,
        borderRadius: '14px',
        padding: '32px 24px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        alignItems: 'center',
      }}
    >
      <Typography sx={{ fontSize: 15, color: tokens.color.muted }}>
        {t('opportunities.list.filtered.empty')}
      </Typography>
      <Button
        data-testid="opportunities-filtered-clear"
        variant="outlined"
        size="small"
        onClick={onClear}
      >
        {t('opportunities.list.filtered.clear')}
      </Button>
    </Box>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="opportunities-error"
      sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
    >
      <Alert severity="warning" variant="outlined">
        {t('opportunities.error.title')}
      </Alert>
      <Box>
        <Button
          data-testid="opportunities-retry"
          variant="outlined"
          color="primary"
          onClick={onRetry}
        >
          {t('opportunities.error.retry')}
        </Button>
      </Box>
    </Box>
  );
}

// ============================================================================
// PAGINATION
// ============================================================================

function Pagination({
  page,
  pageSize,
  total,
  isFetching,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  isFetching: boolean;
  onChange: (page: number) => void;
}) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages === 1) return null;

  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  return (
    <Box
      data-testid="opportunities-pagination"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        py: '10px',
      }}
    >
      <Typography
        sx={{
          fontFamily: tokens.font.mono,
          fontSize: 11,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: tokens.color.muted,
        }}
      >
        {t('opportunities.pagination.page', { page, total: totalPages })}
      </Typography>
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <Button
          data-testid="opportunities-prev"
          variant="outlined"
          size="small"
          disabled={!hasPrev || isFetching}
          onClick={() => onChange(page - 1)}
        >
          {t('opportunities.pagination.previous')}
        </Button>
        <Button
          data-testid="opportunities-next"
          variant="outlined"
          size="small"
          disabled={!hasNext || isFetching}
          onClick={() => onChange(page + 1)}
        >
          {t('opportunities.pagination.next')}
        </Button>
      </Box>
    </Box>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

const FOLLOWED_STORAGE_KEY = 'cp-followed-shortlists';

/**
 * SW-147: persistencia local-only del Follow toggle. Survive sesión via
 * localStorage (no es estado crítico — feature dev/QA hasta SW-148 BE).
 */
function useFollowedShortlists(): [
  Set<string>,
  (updater: (prev: Set<string>) => Set<string>) => void,
] {
  const [state, setState] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const raw = window.localStorage.getItem(FOLLOWED_STORAGE_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return new Set(parsed.filter((x): x is string => typeof x === 'string'));
      return new Set();
    } catch {
      return new Set();
    }
  });

  const update = (updater: (prev: Set<string>) => Set<string>) => {
    setState((prev) => {
      const next = updater(prev);
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(FOLLOWED_STORAGE_KEY, JSON.stringify(Array.from(next)));
        }
      } catch {
        // localStorage puede estar bloqueado (private mode, full quota); no es crítico.
      }
      return next;
    });
  };

  return [state, update];
}

// SW-148: parseShortlistName / hashIndex / formatRelativeDate extraídos a `_helpers.ts`
// con tests unitarios dedicados. `OpportunityDetailPage` también los consume.
